import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useLocation, useSearchParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Star, ArrowRight, LayoutGrid } from 'lucide-react';
import { Container } from '../../components/styled/Container';
import { PageLayout } from '../../components/styled/PageLayout';
import { regionFromPathname } from '../../utils/regionFromPath';
import {
  categoriesHubPath,
  categoriesGenrePath,
  similarTypePath,
  normalizeCategoryType,
  normalizeGenreSlug,
} from '../../utils/categoriesPaths';
import { fetchPublicGenres } from '../../store/actions/genreActions';
import { fetchCatalogByGenre } from '../../store/actions/catalogActions';
import { similarPath } from '../../utils/paths';
import NotFound from '../NotFound/NotFound';
import Pagination, { PAGE_SIZE } from '../../components/categories/Pagination';
import SortBar, { sortItems, SORT_OPTIONS } from '../../components/categories/SortBar';
import SEO from '../../components/seo/SEO';
import { useI18n } from '../../hooks/useI18n';
import StaticAdBanner from '../../components/ads/StaticAdBanner';
import AdInline from '../../components/ads/AdInline';
import { bannerAds, cardAds } from '../../data/staticAds';
import { SIMIMOVIES_MANAGED_ADS_ENABLED } from '../../config/ads';
import { useFetchManagedPlacements } from '../../hooks/useFetchManagedPlacements';
import { trackAdClick, trackAdImpression } from '../../store/actions/adActions';
import { selectAdPlacementFirst } from '../../store/selectors/adSelectors';
import { managedPlacementToAd } from '../../utils/adMapper';
import { makeImpressionKey, shouldTrackImpression } from '../../utils/adTracking';

const CATEGORIES_AD_REQ = Object.freeze([
  { placement: 'inline-banner', limit: 1 },
  { placement: 'results-inline', limit: 1 },
]);

const Header = styled(motion.div)`
  margin-bottom: 1.5rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const IconBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(128, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.maroon};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0 0 0 3.25rem;
`;

const BadgesWrap = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const BadgeLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 600;
  transition: all 0.2s;
  background: ${({ $active, theme }) => ($active ? theme.colors.maroon : theme.colors.muted)};
  color: ${({ $active, theme }) => ($active ? theme.colors.maroonForeground : theme.colors.foreground)};
  text-decoration: none;
  &:hover {
    background: ${({ $active, theme }) => ($active ? theme.colors.maroon : theme.colors.primary)};
    color: ${({ $active, theme }) => ($active ? theme.colors.maroonForeground : theme.colors.primaryForeground)};
  }
`;

const Grid = styled.div`
  margin-top: 2rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Card = styled(motion.div)`
  background: ${({ theme }) => theme.colors.card};
  padding: 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadowCardHover};
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const PosterBox = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
`;

const ScoreBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  background: rgba(234, 179, 8, 0.1);
  color: ${({ theme }) => theme.colors.gold};
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.colors.foreground};
`;

const CardDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  padding: 0.25rem 0.75rem;
  background: rgba(128, 0, 0, 0.1);
  color: ${({ theme }) => theme.colors.maroon};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const CardLink = styled(Link)`
  width: 100%;
  padding: 0.75rem 0;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
  margin-top: auto;
  &:hover {
    opacity: 0.92;
  }
`;

function typeLabel(cat) {
  if (cat === 'anime_tv' || cat === 'anime_movie') return 'Anime';
  if (cat === 'tv') return 'TV';
  return 'Movie';
}

function isValidSort(v) {
  return SORT_OPTIONS.some((o) => o.value === v);
}

function humanizeSlug(slug) {
  return String(slug || '')
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function CategoriesBrowse() {
  const dispatch = useDispatch();
  const genresList = useSelector((s) => s.genres.items);
  const genresLoading = useSelector((s) => s.genres.loading);
  const genresError = useSelector((s) => s.genres.error);
  const { items: genreBrowseItems, loading: genreLoading } = useSelector((s) => s.catalog);
  const { type } = useParams();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const region = regionFromPathname(pathname);
  const { t } = useI18n();
  const pre = region || '';

  const pageParam = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const sortParam = searchParams.get('sort') || 'default';
  const page = Number.isFinite(pageParam) ? pageParam : 1;
  const sortBy = isValidSort(sortParam) ? sortParam : 'default';

  const setPage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(Math.max(1, p)));
    setSearchParams(next, { replace: true });
  };
  const setSort = (s) => {
    const next = new URLSearchParams(searchParams);
    next.set('sort', s);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const mediaType = normalizeCategoryType(type);
  const genreSlug = normalizeGenreSlug(type);

  const isTypeView = mediaType != null;
  const isGenreView = genreSlug != null;

  useEffect(() => {
    dispatch(fetchPublicGenres());
  }, [dispatch]);

  const knownGenre = useMemo(() => {
    if (!genreSlug) return null;
    return (genresList || []).find((g) => g.slug === genreSlug) || null;
  }, [genresList, genreSlug]);

  useEffect(() => {
    if (!isGenreView || !genreSlug || genresLoading) return;
    if (!knownGenre) return;
    dispatch(fetchCatalogByGenre({ genreSlug, region: pre || undefined }));
  }, [dispatch, isGenreView, genreSlug, pre, genresLoading, knownGenre]);

  const genreItems = useMemo(() => {
    if (!isGenreView || !genreSlug) return [];
    return genreBrowseItems || [];
  }, [isGenreView, genreSlug, genreLoading, genreBrowseItems]);

  const genreMeta = useMemo(() => {
    if (!isGenreView || !genreSlug) return null;
    return knownGenre;
  }, [isGenreView, genreSlug, knownGenre]);

  const genreNameBySlug = useMemo(() => {
    const map = {};
    (genresList || []).forEach((g) => {
      if (g?.slug) map[g.slug] = g.name || humanizeSlug(g.slug);
    });
    return map;
  }, [genresList]);

  const sortedGenreItems = useMemo(() => sortItems(genreItems, sortBy), [genreItems, sortBy]);
  const genreTotalItems = sortedGenreItems.length;
  const genreTotalPages = Math.max(1, Math.ceil(genreTotalItems / PAGE_SIZE));
  const genreCurrentPage = Math.min(page, genreTotalPages);
  const paginatedGenreItems = useMemo(
    () => sortedGenreItems.slice((genreCurrentPage - 1) * PAGE_SIZE, genreCurrentPage * PAGE_SIZE),
    [sortedGenreItems, genreCurrentPage],
  );

  const { adDomain, adLocale, adDevice } = useFetchManagedPlacements('categories', CATEGORIES_AD_REQ);
  const managedCatBanner = useSelector((s) => selectAdPlacementFirst(s, 'categories', 'inline-banner'));
  const managedCatInline = useSelector((s) => selectAdPlacementFirst(s, 'categories', 'results-inline'));

  const catBannerAd = useMemo(() => managedPlacementToAd(managedCatBanner, bannerAds[0]), [managedCatBanner]);
  const catInlineAd = useMemo(() => managedPlacementToAd(managedCatInline, cardAds[1]), [managedCatInline]);

  useEffect(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managedCatBanner?._id) return;
    const key = makeImpressionKey({
      campaign: managedCatBanner._id,
      page: 'categories',
      placement: 'inline-banner',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedCatBanner._id,
          page: 'categories',
          placement: 'inline-banner',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedCatBanner, adDevice, adDomain, adLocale]);

  useEffect(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managedCatInline?._id) return;
    const key = makeImpressionKey({
      campaign: managedCatInline._id,
      page: 'categories',
      placement: 'results-inline',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedCatInline._id,
          page: 'categories',
          placement: 'results-inline',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedCatInline, adDevice, adDomain, adLocale]);

  const onCatBannerClick = useCallback(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managedCatBanner?._id) return;
    dispatch(
      trackAdClick({
        campaign: managedCatBanner._id,
        page: 'categories',
        placement: 'inline-banner',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managedCatBanner, adDevice, adDomain, adLocale]);

  const onCatInlineClick = useCallback(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managedCatInline?._id) return;
    dispatch(
      trackAdClick({
        campaign: managedCatInline._id,
        page: 'categories',
        placement: 'results-inline',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managedCatInline, adDevice, adDomain, adLocale]);

  if (isTypeView && mediaType) {
    return <Navigate to={similarTypePath(mediaType, region)} replace />;
  }

  if (isGenreView && genreSlug) {
    if (genresLoading) {
      return (
        <PageLayout>
          <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
            <p style={{ color: 'var(--muted-foreground)' }}>
              {t('categories.browse.loadingGenres', 'Loading genres…')}
            </p>
          </Container>
        </PageLayout>
      );
    }
    if (genresError) {
      return (
        <PageLayout>
          <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
            <p style={{ color: 'var(--muted-foreground)' }}>{genresError}</p>
          </Container>
        </PageLayout>
      );
    }
    if (!genreMeta) {
      return <NotFound />;
    }
  }

  if (isGenreView && genreMeta) {
    const pageTitle = genreMeta.name;
    return (
      <>
        <SEO
          region={region}
          path={`/categories/${genreSlug}`}
          title={`${pageTitle} - ${t('nav.categories', 'Categories')}`}
          description={t('categories.browse.metaDescription', 'Browse titles and open similar picks.')}
          jsonLd={{
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${pageTitle} Categories`,
            about: pageTitle,
            inLanguage: region || 'en',
          }}
        />
        <PageLayout>
          <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
          <Header initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <HeaderRow>
              <IconBox><LayoutGrid size={20} strokeWidth={2.5} /></IconBox>
              <Title>{pageTitle}</Title>
            </HeaderRow>
            <Subtitle>
              {genreTotalItems} {t('categories.browse.subtitle', 'movies, TV shows & anime in')} <strong>{pageTitle}</strong> — {t('categories.browse.subtitleTail', 'open similar picks for any title.')}
            </Subtitle>
          </Header>
          <BadgesWrap>
            <BadgeLink to={categoriesHubPath(region)} $active={false}>
              <LayoutGrid size={18} />
              {t('categories.browse.allGenres', 'All genres')}
            </BadgeLink>
            {genresList.map((g) => (
              <BadgeLink
                key={g.slug}
                to={categoriesGenrePath(g.slug, region)}
                $active={g.slug === genreSlug}
              >
                {g.name}
              </BadgeLink>
            ))}
          </BadgesWrap>
          <div style={{ margin: '1.25rem 0' }}>
            <StaticAdBanner ad={catBannerAd} variant="inline" pageKey="categories" onLinkClick={onCatBannerClick} />
          </div>
          <SortBar
            sortBy={sortBy}
            onSortChange={setSort}
            totalItems={genreTotalItems}
            page={genreCurrentPage}
            pageSize={PAGE_SIZE}
          />
          <AdInline ad={catInlineAd} pageKey="categories" onLinkClick={onCatInlineClick} />
          <Grid>
            {genreTotalItems === 0 ? (
              <p style={{ gridColumn: '1 / -1', color: 'var(--muted-foreground)' }}>
                {t('categories.browse.empty', 'No titles available in this genre yet.')}
              </p>
            ) : (
              paginatedGenreItems.map((it, index) => (
                <Card
                  key={`${it.category}-${it.tmdbId}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.04, 0.3) }}
                >
                  <CardTop>
                    <PosterBox $url={it.poster}>{it.poster ? '' : it.title.charAt(0)}</PosterBox>
                    <ScoreBadge><Star size={12} fill="currentColor" /> {t('similar.browse.similarBadge', 'Similar')}</ScoreBadge>
                  </CardTop>
                  <CardTitle>{it.title}</CardTitle>
                  <CardDesc>{it.overview}</CardDesc>
                  <TagsWrap>
                    <Tag>{typeLabel(it.category)}</Tag>
                    {it.genres &&
                      it.genres.slice(0, 2).map((g) => (
                        <Tag key={g}>{genreNameBySlug[g] || humanizeSlug(g)}</Tag>
                      ))}
                  </TagsWrap>
                  <CardLink to={similarPath(it.category, it.tmdbId, it.title, pre)}>
                    {t('similar.browse.seePicks', 'See similar picks')} <ArrowRight size={14} />
                  </CardLink>
                </Card>
              ))
            )}
          </Grid>
          <Pagination totalItems={genreTotalItems} currentPage={genreCurrentPage} onPageChange={setPage} />
          </Container>
        </PageLayout>
      </>
    );
  }

  return <NotFound />;
}
